import { Link } from "@tanstack/react-router";
import {
  Alert,
  BarChart,
  DataTable,
  Icon,
  PageWrapper,
  type ColumnDef,
  type LucideIconName,
  SectionCard,
  Typography,
} from "@/deps/ui-library";
import { useOverview, useSetupStatus } from "@/api/queries";
import { useCurrentSession } from "@/app/session";
import type { OverviewData } from "@/api/types";
import { KpiStrip } from "@/components/KpiStrip";
import { PageHeader } from "@/components/PageHeader";
import { DocumentStatusBadge } from "@/components/list/StatusBadge";
import { formatAmount, formatCompact } from "@/lib/money";
import { formatDate } from "@/lib/dates";

/** A new company lands here with empty books, and nothing else says what to do first. */
function SetupPrompt() {
  const { data } = useSetupStatus();
  if (!data || (data.company && data.localisation && data.chart && data.fiscalYear)) return null;
  return (
    <Alert
      config={{ variant: "subtle", colorScheme: "info" }}
      description={
        <>
          Your books are not ready to use yet.{" "}
          <Link to="/setup" className="underline">
            Set up your books
          </Link>
        </>
      }
    />
  );
}

/**
 * The financial overview (design lines 102–239).
 *
 * Every figure on this page links to the list it came from. A KPI that cannot be opened is a number
 * somebody has to take on trust, and the first thing anyone does with an unexpected total is try to
 * see what is inside it.
 */
export function FinancialOverviewPage() {
  const { data, isPending } = useOverview();
  const { session, can } = useCurrentSession();

  return (
    // The shell's main has the padding.
    <PageWrapper spacing="md" className="p-0">
      <PageHeader
        title="Financial overview"
        subtitle={
          data
            ? `${session.fiscalYear ? `${session.fiscalYear.label} to ` : "As at "}${formatDate(data.asOf)}`
            : "Loading"
        }
      />

      {can("settings.company.manage") ? <SetupPrompt /> : null}

      <KpiStrip
        loading={isPending}
        items={(data?.kpis ?? []).map((kpi) => ({
          label: kpi.label,
          value: formatAmount(kpi.value),
          tone: kpi.tone === "ok" ? "default" : kpi.tone,
          to: kpi.key === "receivable" ? "/sales/invoices" : undefined,
        }))}
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <SectionCard title="Needs attention" className="lg:col-span-1">
          <ul className="flex flex-col">
            {(data?.queue ?? []).map((item) => (
              <li key={item.key}>
                <Link
                  to={item.to}
                  className="hover:bg-muted focus-ring -mx-2 flex items-start gap-2.5 rounded-md px-2 py-2"
                >
                  {/* A plain icon, not a coloured tile (ADR-0093): the count carries the tone.
                      The API names the icon; RDS renders an empty box for a name lucide lacks. */}
                  <span className="text-muted-foreground mt-0.5 shrink-0">
                    <Icon name={item.icon as LucideIconName} size="sm" color="inherit" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <Typography.Text as="span" weight="medium" className="block">
                      {item.title}
                    </Typography.Text>
                    <Typography.Text as="span" variant="small" muted className="block">
                      {item.description}
                    </Typography.Text>
                  </span>
                  <Typography.Text
                    as="span"
                    numeric
                    weight="semibold"
                    color={QUEUE_TONE[item.tone]}
                  >
                    {item.count}
                  </Typography.Text>
                </Link>
              </li>
            ))}
          </ul>
        </SectionCard>

        <SectionCard title="Income and expense" className="lg:col-span-2">
          {/* The bars are decoration; the figures below them are the content. An aria-label
              summarising twelve months in one string is worse than either — it cannot be read
              column by column, and it goes stale the moment the data changes. */}
          <div aria-hidden="true">
            <IncomeExpenseBars months={data?.incomeExpense ?? []} />
          </div>
          <table className="sr-only">
            <caption>Income and expense by month</caption>
            <thead>
              <tr>
                <th scope="col">Month</th>
                <th scope="col">Income</th>
                <th scope="col">Expense</th>
              </tr>
            </thead>
            <tbody>
              {(data?.incomeExpense ?? []).map((month) => (
                <tr key={month.month}>
                  <th scope="row">{month.month}</th>
                  <td>{formatAmount(month.income)}</td>
                  <td>{formatAmount(month.expense)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="text-fg-muted mt-2 text-xs">
            Net profit, year to date:{" "}
            <Typography.Text as="span" variant="small" numeric weight="medium">
              {formatAmount(data?.netProfitFyToDate ?? "0")}
            </Typography.Text>
          </p>
        </SectionCard>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <SectionCard title="Receivables ageing" className="lg:col-span-2" flush>
          <DataTable
            aria-label="Receivables by age"
            size="sm"
            enableSorting={false}
            columns={AGEING_COLUMNS}
            data={data?.ageing ?? []}
            getRowId={(bucket) => bucket.bucket}
            loading={isPending}
            emptyContent="Nothing is owed."
            pagination={{ mode: "manual", show: false }}
            containerClassName="rounded-none border-0"
          />
        </SectionCard>

        <SectionCard title="Cash and bank">
          <ul className="flex flex-col gap-2">
            {(data?.cash ?? []).map((account) => (
              <li key={account.bankAccountId} className="flex items-baseline justify-between gap-3">
                <span>
                  {/* The bank, then the account. An account with no bank named is only its name. */}
                  <span className="text-fg block text-sm">{account.bank}</span>
                  {account.name === account.bank ? null : (
                    <span className="text-fg-hint block text-xs">{account.name}</span>
                  )}
                </span>
                <Typography.Text as="span" numeric weight="medium">
                  {formatCompact(account.balance)}
                </Typography.Text>
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>

      <SectionCard title="Recent invoices">
        <ul className="flex flex-col">
          {(data?.recent ?? []).map((invoice) => (
            <li key={invoice.id}>
              <Link
                to="/sales/invoices/$id"
                params={{ id: invoice.id }}
                className="hover:bg-muted focus-ring -mx-2 flex items-center gap-3 rounded-md px-2 py-1.5"
              >
                <span className="text-fg w-24 text-sm">{invoice.number ?? "Draft"}</span>
                <span className="text-fg min-w-0 flex-1 truncate text-sm">
                  {invoice.customerName}
                </span>
                <DocumentStatusBadge status={invoice.status} />
                <Typography.Text as="span" numeric className="w-28 text-right">
                  {formatAmount(invoice.total)}
                </Typography.Text>
              </Link>
            </li>
          ))}
        </ul>
      </SectionCard>
    </PageWrapper>
  );
}

/** The colour a count takes when it needs attention. The icon beside it stays plain. */
const QUEUE_TONE = { bad: "danger", warn: "warning", default: "foreground" } as const;

type Month = OverviewData["incomeExpense"][number];
type AgeingBucket = OverviewData["ageing"][number];

const monthName = (month: string) =>
  new Date(`${month}-01T00:00:00Z`).toLocaleDateString("en-GB", {
    month: "short",
    timeZone: "UTC",
  });

/**
 * Income against expense, month by month, on RDS `BarChart`. The chart draws numbers, so the money
 * strings are read as numbers for the bars only; every figure a reader is shown — the hover readout
 * and the table under the chart — is formatted from the decimal string itself.
 *
 * Composed rather than the preset so the SVG can shrink to the card (the preset is a fixed width),
 * capped in height because RDS's sizes are fixed aspect ratios and the card is wide and short.
 */
function IncomeExpenseBars({ months }: { months: Month[] }) {
  const byMonth = new Map(months.map((month) => [month.month, month]));
  return (
    <BarChart.Root
      data={months.map((month) => ({
        month: month.month,
        income: Number(month.income),
        expense: Number(month.expense),
      }))}
      series={[
        { key: "income", label: "Income", color: "var(--primary)" },
        { key: "expense", label: "Expense", color: "var(--accent)" },
      ]}
      xKey="month"
      config={{ size: "md" }}
      grouped
      className="flex w-full"
    >
      <BarChart.Legend position="top" interactive={false} />
      <div className="relative w-full">
        <BarChart.ChartSvg className="block h-auto max-h-64 w-full">
          <BarChart.Grid horizontal vertical={false} tickCount={4} />
          <BarChart.XAxis formatTick={(value) => monthName(String(value))} />
          <BarChart.YAxis tickCount={4} formatTick={(value) => formatCompact(Number(value))} />
          <BarChart.Bars />
        </BarChart.ChartSvg>
        <BarChart.Tooltip
          renderContent={(key) => {
            const month = byMonth.get(key);
            return month ? (
              <div className="flex flex-col gap-1">
                <span className="font-semibold">{monthName(month.month)}</span>
                <span>Income {formatAmount(month.income)}</span>
                <span>Expense {formatAmount(month.expense)}</span>
              </div>
            ) : null;
          }}
        />
      </div>
    </BarChart.Root>
  );
}

/** Built outside the component: a column's `cell` is a render function, not a component. */
const AGEING_COLUMNS: Array<ColumnDef<AgeingBucket>> = [
  {
    id: "age",
    header: "Age",
    meta: { cellClassName: "text-foreground" },
    cell: ({ row: { original: bucket } }) => bucket.bucket,
  },
  {
    id: "amount",
    header: "Amount",
    meta: {
      textAlign: "right",
      numeric: true,
      cellClassName: ({ original: bucket }) =>
        bucket.tone === "bad"
          ? "text-destructive-text"
          : bucket.tone === "warn"
            ? "text-warning-text"
            : "text-foreground",
    },
    cell: ({ row: { original: bucket } }) => formatAmount(bucket.amount),
  },
  {
    id: "share",
    header: "Share",
    meta: { textAlign: "right", numeric: true, cellClassName: "text-muted-foreground" },
    cell: ({ row: { original: bucket } }) => `${bucket.pct}%`,
  },
];
